import { Injectable } from '@angular/core';
import { Observable, catchError, from, map, tap, throwError } from 'rxjs';
import { StoreService } from '@beyou/app/core/store';
import { PatientMapper } from '@beyou/app/core/mappers/patients/patient.mapper';
import { SubriseUser, SubriseUserDetails } from '@beyou/app/core/models/users/subrise-user.model';
import { PatientsApiService } from '@beyou/app/core/services/api/patients-api.service';
import { PagedResponse } from '@beyou/app/core/dto/paged-response';
import { PatientListQuery } from '@beyou/app/core/models/patients/patient.model';
import { UserService } from '@beyou/app/core/store/features/user.service';

/**
 * The patients of a practitioner, loaded into the app wide user store as `SubriseUser`s so the
 * client screens render them like any other user.
 *
 * A patient has two ids: the string user id everything else keys a user by, and the numeric
 * patient id the practice endpoints take. Only the first survives the mapping to `SubriseUser`,
 * so this service keeps the pair — filled in as patients are loaded, and the reason a detail
 * load can start from either id.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientService {

  private patientIdsByUserId = new Map<string, number>();
  private userIdsByPatientId = new Map<number, string>();

  constructor(
    private store: StoreService,
    private patientsApiService: PatientsApiService,
    // Patients live in the same store slice as any other user, so reading one back is
    // `UserService`'s job rather than a second copy of it here.
    private userService: UserService
  ) { }

  /** The patients loaded so far, read back off the user store. */
  get patients$(): Observable<SubriseUser[]> {
    return this.store.state$.pipe(
      map((state) => [...this.patientIdsByUserId.keys()]
        .map((userId) => state.users[userId])
        .filter((user): user is SubriseUser => !!user))
    );
  }

  /** The numeric patient id of a user, once that patient has been loaded. */
  patientIdFor(userId: string): number | undefined {
    return this.patientIdsByUserId.get(userId);
  }

  /** The string user id of a patient, once that patient has been loaded. */
  userIdFor(patientId: number): string | undefined {
    return this.userIdsByPatientId.get(patientId);
  }

  load(practiceId: number, practitionerId: number): Observable<SubriseUser[]> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.patientsApiService.getAll(practiceId, practitionerId).pipe(
      map((patients) => patients.map((patient) => {
        this.rememberIds(patient.userId, patient.id);
        return PatientMapper.toSubriseUser(patient);
      })),
      tap((users) => {
        const state = { ...this.store.state };
        users.forEach((user) => state.users[user.id] = user);
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading patients:', error);
        throw error;
      })
    );
  }

  /**
   * One page of the practice's patients, ordered, searched and filtered by the endpoint.
   *
   * The page goes into the store and its ids are remembered, exactly as a full `load()` would —
   * but only the patients on it, so the caller renders the page it got back rather than
   * `patients$`, which holds everything loaded so far.
   */
  loadPage(practiceId: number, practitionerId: number, query: PatientListQuery = {}): Observable<PagedResponse<SubriseUser>> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.patientsApiService.list(practiceId, practitionerId, query).pipe(
      map((page) => ({
        ...page,
        data: page.data.map((patient) => {
          this.rememberIds(patient.userId, patient.id);
          return PatientMapper.toSubriseUser(patient);
        })
      })),
      tap((page) => {
        const state = { ...this.store.state };
        page.data.forEach((user) => { 
          // Do not overwrite detailed users
          if(!this.userService.detailedUser(user.id)){
            state.users[user.id] = user
          }
        });
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading patients page:', error);
        throw error;
      })
    );
  }

  loadDetails(practiceId: number, practitionerId: number, patientId: number): Observable<SubriseUserDetails> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.patientsApiService.get(practiceId, practitionerId, patientId)).pipe(
      map((patient) => {
        this.rememberIds(patient.userId, patient.id);
        return PatientMapper.toSubriseUserDetails(patient);
      }),
      tap((user) => {
        const state = { ...this.store.state };
        state.users[user.id] = user;
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading patient details:', error);
        throw error;
      })
    );
  }

  /**
   * Details of a patient known by its user id — the id a screen holding a `SubriseUser` has.
   * Only works once that patient has been loaded, since the pair comes from the endpoints.
   */
  loadDetailsForUser(practiceId: number, practitionerId: number, userId: string): Observable<SubriseUserDetails> {
    const patientId = this.patientIdFor(userId);
    if (patientId === undefined) {
      return throwError(() => new Error(`No patient id known for user ${userId}, load the patient list first.`));
    }
    return this.loadDetails(practiceId, practitionerId, patientId);
  }

  /** Drops the patients from the store, along with the ids they were tracked by. */
  clear(): void {
    const state = { ...this.store.state };
    this.patientIdsByUserId.forEach((_, userId) => delete state.users[userId]);
    this.patientIdsByUserId.clear();
    this.userIdsByPatientId.clear();
    this.store.setState(state);
  }

  private rememberIds(userId: string, patientId: number): void {
    this.patientIdsByUserId.set(userId, patientId);
    this.userIdsByPatientId.set(patientId, userId);
  }
}
